import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AssetIcon from '../../../../components/AssetIcon';
import { getProfitColor } from '../../../../helpers/colors';
import {
  formatBalance,
  formatPrice,
  formatNumber,
  formatPercentage,
} from '../../../../helpers/NumberFormatter';
import './index.css';
import { Transaction } from 'grts-utils/types/Transaction';
import { useDispatch } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../store/slices/subscription';

interface Props {
  transaction: Transaction;
}

const TransactionItem: React.FC<Props> = (props: Props) => {
  const dispatch = useDispatch();

  const {
    orderType,
    isComplete,
    transactionTimestamp,
    orderPrice,
    assetQuantity,
    stableQuantity,
    currentAssetBalance,
    currentStableBalance,
    priceChange,
    profit,
    subscriptionId,
    baseAsset,
  } = props.transaction;

  const status = useMemo(() => {
    if (orderType === 'BUY') {
      if (isComplete) {
        return 'BOUGHT';
      } else {
        return 'BUYING';
      }
    } else {
      if (isComplete) {
        return 'SOLD';
      } else {
        return 'SELLING';
      }
    }
  }, [isComplete, orderType]);

  const statusColor = useMemo(() => {
    if (orderType === 'BUY') {
      return 'green';
    } else {
      return 'red';
    }
  }, [orderType]);

  const date = useMemo(() => {
    const rawDate = new Date(transactionTimestamp);

    return new Intl.DateTimeFormat('nl-BE', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(rawDate);
  }, [transactionTimestamp]);

  const getPriceChangeColor = useCallback(
    (value: number) => {
      if (
        (value >= 0 && orderType === 'BUY') ||
        (value <= 0 && orderType === 'SELL')
      ) {
        return 'green';
      } else {
        return 'red';
      }
    },
    [orderType],
  );

  const handleSubscriptionClick = () => {
    dispatch(setActiveSubscriptionId(subscriptionId));
  };

  return (
    <div className="ti-container">
      <div className="ti-asset-container">
        <Link
          to={'/sub'}
          onClick={handleSubscriptionClick}
          style={{ textDecoration: 'none' }}
        >
          <AssetIcon baseAsset={baseAsset || ''} width={40} height={40} />
        </Link>
        <h2 className="ti-status-text" style={{ color: statusColor }}>
          {status}
        </h2>
        <h4 className="ti-date-text">{date}</h4>
      </div>
      <div className="row">
        <h4 className="ti-label">Order Price: </h4>
        <h4 className="ti-order-price-text">{formatPrice(orderPrice)}</h4>
      </div>
      <div className="row">
        <h4 className="ti-label">Quantity: </h4>
        <h4 className="ti-order-price-text">
          {formatNumber(assetQuantity)} {baseAsset}
        </h4>
        <h4 className="ti-order-price-text">
          / {formatBalance(stableQuantity)}
        </h4>
      </div>
      <div className="row">
        <h4 className="ti-label">Balance: </h4>
        <h4 className="ti-order-price-text">
          {currentAssetBalance} {baseAsset}
        </h4>
        <h4 className="ti-order-price-text">
          / {formatBalance(currentStableBalance)}
        </h4>
      </div>
      {!!profit && (
        <div className="row">
          <h4 className="ti-label">Transaction profit: </h4>
          <h4
            className="ti-order-price-text"
            style={{ color: getProfitColor(profit) }}
          >
            {profit ? formatPercentage(profit) : '-'}
          </h4>
        </div>
      )}
      <div className="row">
        <h4 className="ti-label-price-change">Price Change: </h4>
        <h4
          className="ti-order-price-text"
          style={{ color: getPriceChangeColor(priceChange ?? 0) }}
        >
          {formatPercentage(priceChange ?? 0)}
        </h4>
        {/* {priceChange && (
          <>
            <h4 className="ti-label-current-profit">Current profit: </h4>
            <h4
              className="ti-order-price-text"
              style={{ color: getProfitColor(currentProfit) }}
            >
              {formatPercentage(currentProfit ?? 0)}
            </h4>
          </>
        )} */}
      </div>
    </div>
  );
};

export default TransactionItem;
